/**
 * Progress Data Migration Utility
 * Migrates existing user progress data to include new TPM fields
 */

export function migrateProgressData(): void {
  console.log('🔄 Starting progress data migration...');
  
  let migratedCount = 0;
  let skippedCount = 0;
  
  // Scan all localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    // Match keys like "basicsProgress_keyboard-id" or "pythonProgress_keyboard-id"
    const match = key.match(/^(basics|python)Progress_(.+)$/);
    if (match) {
      const [, language, keyboardId] = match;
      const data = localStorage.getItem(key);
      
      if (data) {
        try {
          const progress = JSON.parse(data);
          
          // Check if already migrated (has averageTPM field)
          if (progress.averageTPM !== undefined && progress.bestTPM !== undefined) {
            console.log(`✓ Already migrated: ${language} - ${keyboardId}`);
            skippedCount++;
            continue;
          }
          
          console.log(`📝 Migrating: ${language} - ${keyboardId}`);
          
          // Add new TPM fields to user progress
          progress.averageTPM = 0;
          progress.bestTPM = 0;
          
          // Migrate level scores
          if (progress.levelScores && Array.isArray(progress.levelScores)) {
            progress.levelScores = progress.levelScores.map((score: any) => {
              // If score doesn't have tpm field, add it
              if (score.tpm === undefined) {
                score.tpm = 0;
              }
              
              // If score doesn't have speedMetricType, infer it from language
              if (score.speedMetricType === undefined) {
                score.speedMetricType = language === 'basics' ? 'wpm' : 'tpm';
              }
              
              return score;
            });
            
            // Recalculate averages and bests
            const textScores = progress.levelScores.filter((s: any) => s.speedMetricType === 'wpm');
            const codeScores = progress.levelScores.filter((s: any) => s.speedMetricType === 'tpm');
            
            if (textScores.length > 0) {
              progress.averageWPM = Math.round(
                textScores.reduce((sum: number, s: any) => sum + (s.wpm || 0), 0) / textScores.length
              );
              progress.bestWPM = Math.max(...textScores.map((s: any) => s.wpm || 0));
            }
            
            if (codeScores.length > 0) {
              progress.averageTPM = Math.round(
                codeScores.reduce((sum: number, s: any) => sum + (s.tpm || 0), 0) / codeScores.length
              );
              progress.bestTPM = Math.max(...codeScores.map((s: any) => s.tpm || 0));
            }
          }
          
          // Migrate daily progress
          if (progress.dailyProgress && Array.isArray(progress.dailyProgress)) {
            progress.dailyProgress = progress.dailyProgress.map((day: any) => {
              if (day.averageTPM === undefined) {
                day.averageTPM = 0;
              }
              return day;
            });
          }
          
          // Migrate weekly progress
          if (progress.weeklyProgress && Array.isArray(progress.weeklyProgress)) {
            progress.weeklyProgress = progress.weeklyProgress.map((week: any) => {
              if (week.averageTPM === undefined) {
                week.averageTPM = 0;
              }
              return week;
            });
          }
          
          // Save migrated data back to localStorage
          localStorage.setItem(key, JSON.stringify(progress));
          migratedCount++;
          
          console.log(`✅ Migrated: ${language} - ${keyboardId}`);
        } catch (error) {
          console.error(`❌ Error migrating ${key}:`, error);
        }
      }
    }
  }
  
  console.log(`\n🎉 Migration complete!`);
  console.log(`   Migrated: ${migratedCount} session(s)`);
  console.log(`   Skipped: ${skippedCount} session(s) (already migrated)`);
}

/**
 * Check if migration is needed
 */
export function isMigrationNeeded(): boolean {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    const match = key.match(/^(basics|python)Progress_(.+)$/);
    if (match) {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const progress = JSON.parse(data);
          // If any progress data is missing the new fields, migration is needed
          if (progress.averageTPM === undefined || progress.bestTPM === undefined) {
            return true;
          }
        } catch (error) {
          console.error(`Error checking migration for ${key}:`, error);
        }
      }
    }
  }
  
  return false;
}

